import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { 
  Wrench, Zap, Calendar, Camera, MessageSquare, DollarSign, 
  Check, ArrowRight, Star, Clock, Shield, Smartphone, ChevronLeft, ChevronRight,
  Receipt, Users, Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

export default function Landing() {
  const navigate = useNavigate();
  const { demoLogin } = useAuth();
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  const [loadingDemo, setLoadingDemo] = useState(false);
  const hasTracked = useRef(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Track page visit for analytics (with guard against StrictMode double-fire)
  useEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;
    
    const trackVisit = async () => {
      try {
        await fetch(`${API_URL}/api/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: 'landing',
            referrer: document.referrer || null,
            user_agent: navigator.userAgent
          })
        });
      } catch (err) {
        // Silently fail - analytics shouldn't break the app
        console.debug('Analytics tracking failed:', err);
      }
    };
    trackVisit();
  }, [API_URL]);

  // Demo login handler
  const handleDemoLogin = async () => {
    setLoadingDemo(true);
    try {
      await demoLogin();
      navigate('/dashboard');
    } catch (error) {
      console.error('Demo login error:', error);
      alert('Could not connect to demo. Please try again.');
    } finally {
      setLoadingDemo(false);
    }
  };

  // Demo screenshots carousel data - Real app features
  // Note: Using static screenshots from the actual app
  const demoScreenshots = [
    {
      id: 'joblist',
      title: 'Job List Dashboard',
      description: 'See all your jobs at a glance with real earnings and pending amounts. One-thumb status buttons let you update jobs instantly.',
      image: 'https://static.prod-images.emergentagent.com/jobs/66c95ad0-c46b-4bea-9c98-b2e15c3540be/images/d5e4bbe526525e83b7c101edd5ad81d7661ac926efdc18f9029d67e368d6cb7d.png',
      icon: Wrench,
      color: 'from-orange-500 to-amber-500',
      stats: ['$1,533 Earned', '$9,020 Pending', '6 Active Jobs']
    },
    {
      id: 'calendar',
      title: 'Visits Calendar',
      description: 'Schedule jobs and see your week at a glance. Tap any date to view or schedule visits. Never double-book again.',
      image: 'https://static.prod-images.emergentagent.com/jobs/66c95ad0-c46b-4bea-9c98-b2e15c3540be/images/5936fbb2fa3ff991331d8ba46b99dbba9d8d820ca741c5a1e209a26b128e0a36.png',
      icon: Calendar,
      color: 'from-blue-500 to-cyan-500',
      stats: ['Monthly View', 'Team Filter', 'Job Dots']
    },
    {
      id: 'quickquote',
      title: '30-Second Quote',
      description: 'Create and send professional quotes before you leave the driveway. Just fill in the basics and tap send.',
      image: 'https://static.prod-images.emergentagent.com/jobs/66c95ad0-c46b-4bea-9c98-b2e15c3540be/images/b333e8990622572516143039f4ba97a0cbc5a21d5dc54d5b750f66f87eb3095c.png',
      icon: Zap,
      color: 'from-green-500 to-emerald-500',
      stats: ['Auto Tax Calc', 'Save Client', 'Schedule Option']
    },
    {
      id: 'jobdetail',
      title: 'Easy Actions',
      description: 'PDF export, SMS, Email - every action is one tap away. Track billable expenses and request payment instantly.',
      image: 'https://static.prod-images.emergentagent.com/jobs/66c95ad0-c46b-4bea-9c98-b2e15c3540be/images/1e4dc1c8b7936425a3cd6da6c4bd6e52f0cb870a9c14ae5f861da968a1c38fef.png',
      icon: Receipt,
      color: 'from-purple-500 to-pink-500',
      stats: ['PDF Export', 'SMS & Email', 'Request Payment']
    }
  ];

  // Auto-rotate carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % demoScreenshots.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [demoScreenshots.length]);

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % demoScreenshots.length);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + demoScreenshots.length) % demoScreenshots.length);
  };

  // Fetch pricing plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch(`${API_URL}/api/plans`);
        if (response.ok) {
          const data = await response.json();
          // Handle both array and {plans: [...]} response formats
          const apiPlans = Array.isArray(data) ? data : (data.plans || []);
          
          // Get default plans for structure and merge with API prices
          const defaults = getDefaultPlans();
          const mergedPlans = defaults.map(defaultPlan => {
            const apiPlan = apiPlans.find(p => p.id === defaultPlan.id);
            if (apiPlan) {
              const price = apiPlan.price || defaultPlan.price;
              // Build features list dynamically based on plan capabilities
              const features = [];
              
              // Jobs limit
              if (apiPlan.max_jobs_per_month) {
                features.push(`${apiPlan.max_jobs_per_month} jobs per month`);
              } else {
                features.push("Unlimited jobs");
              }
              
              // Team members
              if (apiPlan.max_team_members > 1) {
                features.push(`Up to ${apiPlan.max_team_members} team members`);
              }
              
              // Base features
              features.push("Quote & invoice");
              features.push("Customer portal");
              
              // Communication features
              if (apiPlan.has_native_sms) {
                if (apiPlan.max_sms_per_month) {
                  features.push(`Native SMS (${apiPlan.max_sms_per_month}/mo)`);
                } else {
                  features.push("Native SMS (unlimited)");
                }
              } else {
                features.push("SMS reminders (external)");
              }
              
              if (apiPlan.has_native_email) {
                if (apiPlan.max_emails_per_month) {
                  features.push(`In-app email (${apiPlan.max_emails_per_month}/mo)`);
                } else {
                  features.push("In-app email (unlimited)");
                }
              }
              
              if (apiPlan.has_auto_reminders) {
                features.push("Auto appointment reminders");
              }
              
              // Expense features
              if (apiPlan.has_mileage) {
                features.push("Expense tracking & mileage");
              }
              
              if (apiPlan.has_billable_expenses) {
                features.push("Billable expenses");
              }
              
              // Other premium features
              if (apiPlan.has_logo) {
                features.push("Custom logo on invoices");
              }
              
              if (apiPlan.has_stripe) {
                features.push("Stripe payments");
              } else {
                features.push("Venmo/Cash App payments");
              }
              
              // Team features for crew
              if (apiPlan.max_team_members > 1) {
                features.push("Team scheduling");
                features.push("Job assignment");
                features.push("Shared calendar");
              }
              
              // CRM feature
              if (apiPlan.has_crm) {
                features.push("Client Management (CRM)");
              }
              
              // Recurring appointments
              if (apiPlan.has_recurring) {
                features.push("Recurring appointments");
              }
              
              return {
                ...defaultPlan,
                price: price,
                price_display: price === 0 ? "Free" : `$${price}`,
                max_jobs_per_month: apiPlan.max_jobs_per_month,
                max_team_members: apiPlan.max_team_members,
                features: features.slice(0, 6) // Limit to 6 features for display
              };
            }
            return defaultPlan;
          });
          
          setPricingPlans(mergedPlans);
        } else {
          // Fallback to default plans if API fails
          setPricingPlans(getDefaultPlans());
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        setPricingPlans(getDefaultPlans());
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, [API_URL]);
  
  // Default plans fallback
  const getDefaultPlans = () => [
    {
      id: "free",
      name: "Side Hustle",
      price: 0,
      price_display: "Free",
      price_period: "forever",
      description: "Perfect for weekend warriors",
      features: [
        "5 jobs per month",
        "Quote & invoice",
        "SMS reminders",
        "Customer portal",
        "Venmo/Cash App payments"
      ],
      cta: "Start Free",
      highlighted: false
    },
    {
      id: "solo",
      name: "Solo Pro",
      price: 19,
      price_display: "$19",
      price_period: "/month",
      description: "For full-time contractors",
      features: [
        "Unlimited jobs",
        "Expense tracking & mileage",
        "Note templates",
        "Job profitability reports",
        "Custom logo on invoices",
        "Stripe payments"
      ],
      cta: "Go Pro",
      highlighted: true
    },
    {
      id: "crew",
      name: "Crew",
      price: 49,
      price_display: "$49",
      price_period: "/month",
      description: "For growing teams",
      features: [
        "Up to 10 team members",
        "Everything in Solo Pro",
        "Team scheduling",
        "Job assignment",
        "Shared calendar",
        "Admin dashboard"
      ],
      cta: "Start Growing",
      highlighted: false
    }
  ];

  const features = [
    {
      icon: Zap,
      title: "30-Second Quotes",
      description: "Create and send professional quotes via text message before you leave the driveway."
    },
    {
      icon: Calendar,
      title: "Visits Calendar",
      description: "See your week at a glance. Tap any address to open directions in Maps."
    },
    {
      icon: Camera,
      title: "Photo Documentation",
      description: "Snap before/after photos. They're saved with the job for warranty and tax records."
    },
    {
      icon: MessageSquare,
      title: "SMS Reminders",
      description: "One tap sends a reminder text. No more no-shows."
    },
    {
      icon: DollarSign,
      title: "Get Paid Fast",
      description: "Venmo or Cash App links sent directly to your customer. Money in your pocket same day."
    },
    {
      icon: Smartphone,
      title: "One-Thumb Operation",
      description: "Built for calloused hands. Every button reachable while holding a wrench."
    }
  ];

  const testimonials = [
    {
      name: "Mike R.",
      role: "Plumber, Austin TX",
      quote: "I used to lose quotes on sticky notes. Now I send them before I even leave the customer's house.",
      rating: 5
    },
    {
      name: "Sarah T.",
      role: "Electrician, Denver CO", 
      quote: "The one-thumb thing is real. I can update job status while carrying my toolbox.",
      rating: 5
    },
    {
      name: "Carlos M.",
      role: "Handyman, Phoenix AZ",
      quote: "My no-show rate dropped to almost zero with the SMS reminders. Game changer.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-slate-darker" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-darker/95 backdrop-blur-sm border-b border-slate-700">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-0.5">
            <img 
              src="/favicon.png" 
              alt="MyJobTools" 
              className="h-9 w-9"
            />
            <span className="font-heading text-xl font-bold italic">
              <span className="text-slate-200">MyJob</span>
              <span className="text-[#FF5F00]">Tools</span>
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            <Link 
              to="/login" 
              data-testid="nav-login"
              className="text-slate-300 hover:text-white font-medium transition-colors"
            >
              Log In
            </Link>
            <Link to="/register" data-testid="nav-signup">
              <Button className="bg-[#FF5F00] hover:bg-orange-hover text-white font-bold px-6">
                Sign Up Free
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-slate-800 text-[#FF5F00] px-4 py-2 rounded-full text-sm font-bold mb-6">
            <Zap className="w-4 h-4" />
            Built for tradespeople, by tradespeople
          </div>
          
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            The toolbox for<br />
            <span className="text-[#FF5F00]">your paperwork.</span>
          </h1>
          
          <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
            Quotes, scheduling, and payments in one app. No computer degree required. 
            Send a quote before you leave the driveway.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register" data-testid="hero-cta">
              <Button className="w-full sm:w-auto h-14 px-8 bg-[#FF5F00] hover:bg-orange-hover text-white text-lg font-bold rounded-lg shadow-hard">
                Start Free — No Card Required
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button 
              onClick={handleDemoLogin}
              disabled={loadingDemo}
              data-testid="live-demo-btn"
              className="w-full sm:w-auto h-14 px-8 bg-slate-700 hover:bg-slate-600 text-white text-lg font-bold rounded-lg border border-slate-600"
            >
              {loadingDemo ? (
                <>
                  <div className="w-5 h-5 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Loading Demo...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5 mr-2 fill-current" />
                  Try Live Demo
                </>
              )}
            </Button>
          </div>
          
          <p className="text-slate-500 text-sm mt-4">
            <Link to="/login" className="text-slate-400 hover:text-white font-medium">
              Already have an account? Log in
            </Link>
          </p>
          
          {/* Trust badges */}
          <div className="flex items-center justify-center gap-8 mt-12 text-slate-500">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm">No credit card</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span className="text-sm">Setup in 60 seconds</span>
            </div>
            <div className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              <span className="text-sm">Works on any phone</span>
            </div>
          </div>
        </div>
      </section>

      {/* App Preview - Clean Feature Showcase */}
      <section className="py-16 px-4" id="demo">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              See it in action
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Built for one-thumb operation. Manage your entire business from the job site.
            </p>
          </div>

          <div className="relative">
            {/* Main Carousel Container */}
            <div className="bg-gradient-to-b from-slate-800/50 to-slate-900/50 rounded-3xl p-6 sm:p-10 border border-slate-700/50 overflow-hidden backdrop-blur-sm">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                
                {/* Left: Feature Description - Now primary focus */}
                <div className="text-center lg:text-left order-1">
                  {demoScreenshots.map((screenshot, index) => {
                    const IconComponent = screenshot.icon;
                    return (
                      <div
                        key={screenshot.id}
                        className={`transition-all duration-500 ${
                          index === activeSlide 
                            ? 'opacity-100 translate-y-0' 
                            : 'opacity-0 translate-y-4 absolute pointer-events-none'
                        }`}
                      >
                        <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${screenshot.color} text-white px-4 py-2 rounded-full text-sm font-bold mb-4`}>
                          <IconComponent className="w-4 h-4" />
                          {screenshot.title}
                        </div>
                        <h3 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
                          {screenshot.title}
                        </h3>
                        <p className="text-slate-400 text-lg mb-6">
                          {screenshot.description}
                        </p>
                        
                        {/* Feature highlights */}
                        {screenshot.stats && (
                          <div className="flex flex-wrap gap-2 mb-6 justify-center lg:justify-start">
                            {screenshot.stats.map((stat, i) => (
                              <span key={i} className="bg-slate-700/50 text-slate-300 px-3 py-1 rounded-full text-sm">
                                {stat}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                          <Button 
                            onClick={handleDemoLogin}
                            disabled={loadingDemo}
                            data-testid="carousel-demo-btn"
                            className="bg-slate-700 hover:bg-slate-600 text-white font-bold px-6 h-12 border border-slate-600"
                          >
                            {loadingDemo ? 'Loading...' : 'Try Live Demo'}
                          </Button>
                          <Link to="/register">
                            <Button className="bg-[#FF5F00] hover:bg-orange-hover text-white font-bold px-6 h-12 w-full sm:w-auto">
                              Start Free
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Right: Screenshot - Secondary visual */}
                <div className="relative flex justify-center order-2">
                  <div className="relative">
                    {/* Screenshot container */}
                    <div className="relative w-[260px] sm:w-[300px] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-slate-100">
                      {demoScreenshots.map((screenshot, index) => (
                        <img
                          key={screenshot.id}
                          src={screenshot.image}
                          alt={screenshot.title}
                          className={`w-full h-auto transition-all duration-500 ease-out ${
                            index === activeSlide 
                              ? 'opacity-100 scale-100 relative' 
                              : 'opacity-0 scale-95 absolute inset-0'
                          }`}
                        />
                      ))}
                    </div>
                    
                    {/* Glow effect */}
                    <div className={`absolute -inset-4 bg-gradient-to-r ${demoScreenshots[activeSlide].color} opacity-15 blur-2xl -z-10 transition-all duration-700`}></div>
                  </div>
                </div>
              </div>

              {/* Navigation Dots & Arrows */}
              <div className="flex items-center justify-center gap-6 mt-8">
                <button
                  onClick={prevSlide}
                  className="p-2 rounded-full bg-slate-700 hover:bg-slate-600 text-white transition-colors"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <div className="flex gap-2">
                  {demoScreenshots.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveSlide(index)}
                      className={`transition-all duration-300 rounded-full ${
                        index === activeSlide 
                          ? 'w-8 h-3 bg-[#FF5F00]' 
                          : 'w-3 h-3 bg-slate-600 hover:bg-slate-500'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={nextSlide}
                  className="p-2 rounded-full bg-slate-700 hover:bg-slate-600 text-white transition-colors"
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Feature Pills Below */}
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {demoScreenshots.map((screenshot, index) => {
                const IconComponent = screenshot.icon;
                return (
                  <button
                    key={screenshot.id}
                    onClick={() => setActiveSlide(index)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all ${
                      index === activeSlide
                        ? 'bg-[#FF5F00] text-white shadow-lg scale-105'
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {screenshot.title}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4" id="features">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl font-bold text-white mb-4">
              Everything you need. Nothing you don't.
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              No 47-tab dashboards. No certifications required. Just the tools that actually make you money.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index}
                  className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-[#FF5F00] transition-colors"
                >
                  <div className="w-12 h-12 bg-[#FF5F00]/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#FF5F00]" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-400">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl font-bold text-white mb-4">
              Trusted by real tradespeople
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-slate-800 rounded-xl p-6 border border-slate-700"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-500 fill-amber-500" />
                  ))}
                </div>
                <p className="text-slate-300 mb-4 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="font-bold text-white">{testimonial.name}</p>
                  <p className="text-slate-500 text-sm">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4" id="pricing">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl font-bold text-white mb-4">
              Simple pricing. No surprises.
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              No contracts. No setup fees. Cancel anytime with one tap.
            </p>
          </div>
          
          {loadingPlans ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF5F00]"></div>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div 
                key={plan.id || index}
                data-testid={`pricing-${plan.name.toLowerCase().replace(' ', '-')}`}
                className={`rounded-2xl p-6 ${
                  plan.highlighted 
                    ? 'bg-[#FF5F00] text-white scale-105 shadow-2xl' 
                    : 'bg-slate-800 border border-slate-700'
                }`}
              >
                {plan.highlighted && (
                  <div className="text-center mb-4">
                    <span className="bg-white text-[#FF5F00] text-xs font-bold px-3 py-1 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <h3 className={`font-heading text-2xl font-bold mb-2 ${
                  plan.highlighted ? 'text-white' : 'text-white'
                }`}>
                  {plan.name}
                </h3>
                
                <div className="mb-4">
                  <span className={`font-heading text-5xl font-bold ${
                    plan.highlighted ? 'text-white' : 'text-white'
                  }`}>
                    {plan.price_display || (plan.price === 0 ? 'Free' : `$${plan.price}`)}
                  </span>
                  <span className={plan.highlighted ? 'text-white/80' : 'text-slate-400'}>
                    {plan.price_period || plan.period}
                  </span>
                </div>
                
                <p className={`mb-6 ${plan.highlighted ? 'text-white/80' : 'text-slate-400'}`}>
                  {plan.description}
                </p>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className={`w-5 h-5 ${
                        plan.highlighted ? 'text-white' : 'text-[#FF5F00]'
                      }`} />
                      <span className={plan.highlighted ? 'text-white' : 'text-slate-300'}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                
                <Link to="/register">
                  <Button 
                    className={`w-full h-12 font-bold ${
                      plan.highlighted 
                        ? 'bg-white text-[#FF5F00] hover:bg-slate-100' 
                        : 'bg-[#FF5F00] text-white hover:bg-orange-hover'
                    }`}
                  >
                    {plan.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          )}
          
          {/* Compare Plans CTA */}
          <div className="text-center mt-10">
            <Link 
              to="/pricing" 
              data-testid="compare-plans-link"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white font-medium transition-colors"
            >
              Compare all features
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Content Section - Who We Serve */}
      <section className="py-16 px-4 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Built for Every Trade
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Whether you're fixing pipes, wiring homes, or maintaining pools — MyJobTools speaks your language.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { trade: 'Plumbers', desc: 'Quote water heater installs, track service calls' },
              { trade: 'Electricians', desc: 'Manage panel upgrades, schedule inspections' },
              { trade: 'HVAC Technicians', desc: 'Seasonal maintenance, equipment installs' },
              { trade: 'Handymen', desc: 'Multi-trade jobs, hourly or flat-rate billing' },
              { trade: 'Pool Service', desc: 'Recurring routes, chemical tracking' },
              { trade: 'Landscapers', desc: 'Weekly mowing schedules, seasonal contracts' },
              { trade: 'Painters', desc: 'Room-by-room quotes, material tracking' },
              { trade: 'Cleaners', desc: 'Recurring appointments, team scheduling' }
            ].map((item, idx) => (
              <div 
                key={idx}
                className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 hover:border-[#FF5F00]/50 transition-colors"
              >
                <h3 className="font-bold text-white mb-1">{item.trade}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* SEO-friendly text content */}
          <div className="mt-12 bg-slate-800/30 rounded-2xl p-8 border border-slate-700/50">
            <h3 className="font-heading text-xl font-bold text-white mb-4">
              Why Contractors Choose MyJobTools Over Complex Software
            </h3>
            <div className="grid md:grid-cols-2 gap-6 text-slate-400 text-sm">
              <div>
                <p className="mb-4">
                  <strong className="text-white">No More Sticky Notes:</strong> Tired of losing quotes on scraps of paper? 
                  MyJobTools lets you create professional quotes in 30 seconds flat — right from the job site. 
                  Your customer gets a text message with pricing before you even pull out of the driveway.
                </p>
                <p>
                  <strong className="text-white">Get Paid Same Day:</strong> Stop chasing payments. Send Venmo or 
                  CashApp links directly to customers. For larger jobs, connect your Stripe account and accept 
                  credit cards with no monthly fees.
                </p>
              </div>
              <div>
                <p className="mb-4">
                  <strong className="text-white">Built for the Field:</strong> Unlike office software designed for 
                  accountants, MyJobTools is built for calloused hands. Every button is reachable with one thumb. 
                  No training required — if you can text, you can use MyJobTools.
                </p>
                <p>
                  <strong className="text-white">Free Forever (Really):</strong> Start with 5 jobs per month at no cost. 
                  No credit card required. Upgrade only when you're ready to grow. We succeed when you succeed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-4xl font-bold text-white mb-4">
            Ready to simplify your paperwork?
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            Join thousands of contractors who got their evenings back.
          </p>
          <Link to="/register" data-testid="final-cta">
            <Button className="h-14 px-8 bg-[#FF5F00] hover:bg-orange-hover text-white text-lg font-bold rounded-lg shadow-hard">
              Start Your Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <p className="text-slate-500 text-sm mt-4">
            Free forever for up to 5 jobs/month. No credit card required.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-0.5">
            <img 
              src="/favicon.png" 
              alt="MyJobTools" 
              className="h-8 w-8"
            />
            <span className="font-heading text-lg font-bold italic">
              <span className="text-slate-200">MyJob</span>
              <span className="text-[#FF5F00]">Tools</span>
            </span>
          </div>
          
          <p className="text-slate-500 text-sm">
            © 2026 MyJobTools. The toolbox for your paperwork.
          </p>
          
          <div className="flex gap-6">
            <Link to="/privacy" className="text-slate-400 hover:text-white text-sm">Privacy</Link>
            <Link to="/terms" className="text-slate-400 hover:text-white text-sm">Terms</Link>
            <a href="mailto:support@myjobtools.com" className="text-slate-400 hover:text-white text-sm">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
